import { useEffect, useContext, useRef } from 'react';
import { useAuth } from '@clerk/react';
import { useSearchParams } from 'react-router-dom';
import { Mixpanel } from '../../../services/Mixpanel';
import { initLogRocket, identifyLogRocketUser } from '../../../services/LogRocket';
import { DiffitUserContext } from '../../../contexts/DiffitUserContext';
import { useSignInModal } from '../../../contexts/SignInModalContext';
import { getCurricula } from '../../../services/CurriculumService';
import { registerCurricula, toCurricula } from '../../../utils/curriculum';

/**
 * Runs all one-time initialization effects for the Unlimited page:
 * page view tracking, LogRocket setup, shared template detection,
 * prompt param pre-fill, deep-link menu auto-open, welcome modal trigger,
 * and scroll-to-library.
 *
 * @param {Object} params
 * @param {Function} params.setQuery - Setter for the query input.
 * @param {Object} params.autocomplete - Autocomplete hook return value.
 * @param {Function} params.adjustTextareaHeight - Textarea resize callback.
 * @param {Object} params.inputRef - Ref to the input textarea element.
 * @param {Function} params.setShowWelcomeModal - Setter for welcome modal visibility.
 * @param {Function} params.setSharedTemplateIds - Setter for the shared template ID list.
 * @param {Function} params.setShowAdaptMenu - Setter for the Adapt menu visibility.
 * @param {Function} params.setShowPlusMenu - Setter for the Attach (plus) menu visibility.
 * @param {Function} params.setShowStandardsSubmenu - Setter for the Align (standards) menu visibility.
 * @param {Function} params.openCurriculum - Opens the lesson picker for one curriculum id.
 * @param {string[]} [params.extraCurricula] - LD `extra-curricula`: the restricted curricula this
 *   teacher may open.
 */
export const useInitEffects = ({
  setQuery,
  autocomplete,
  adjustTextareaHeight,
  inputRef,
  setShowWelcomeModal,
  setSharedTemplateIds,
  setShowAdaptMenu,
  setShowPlusMenu,
  setShowStandardsSubmenu,
  openCurriculum,
  extraCurricula,
}) => {
  const { isSignedIn, isLoaded } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const showSignInModal = useSignInModal();
  const { diffitUser, finishedLoadingOnboardingState, hasActiveSubscription, isLDidentified } =
    useContext(DiffitUserContext);

  const pendingTimers = useRef([]);
  useEffect(() => () => pendingTimers.current.forEach(clearTimeout), []);

  // Tracks the prompt param value we've already pre-filled, so the effect below
  // pre-fills a given prompt only once. Without this, a logged-out user (whose
  // ?prompt= is left in the URL for post-sign-up recovery) has every keystroke
  // clobbered: typing changes `query`, which recreates the memoized `autocomplete`
  // object, which re-fires the effect and re-runs setQuery(promptParam).
  const appliedPromptRef = useRef(null);

  // Prompt param whose autocomplete fetch is still owed. Fetching waits for Clerk
  // (`isLoaded`) so signed-in users get an authenticated request, and this ref keeps
  // the fetch a one-shot across the effect's many re-runs.
  const pendingSuggestionsRef = useRef(null);

  // Page view
  useEffect(() => {
    Mixpanel.track('Unlimited Page Viewed');
  }, []);

  // LogRocket
  useEffect(() => {
    initLogRocket({ isPaidUser: hasActiveSubscription });
    if (diffitUser?.id)
      identifyLogRocketUser(diffitUser.id, { email: diffitUser.email, name: diffitUser.name });
    inputRef.current?.focus();
  }, [diffitUser, hasActiveSubscription, inputRef]);

  // Welcome modal
  useEffect(() => {
    const hasCompletedOnboarding = diffitUser?.seen_nux_json?.has_completed_onboarding_bts_2026;
    const hasSeenWorkshopWelcome = diffitUser?.seen_nux_json?.workshop_welcome_seen;
    if (
      isSignedIn &&
      finishedLoadingOnboardingState &&
      hasCompletedOnboarding &&
      !hasSeenWorkshopWelcome
    ) {
      // Welcome modal disabled pending new-user-experience revamp — see Slack initiative-freemium-bts26
      // setShowWelcomeModal(true);
    }
  }, [isSignedIn, finishedLoadingOnboardingState, diffitUser, setShowWelcomeModal]);

  // Sign-in deep link (?sign_in=true) — used by the Chrome extension's
  // "Sign in to Diffit" button so the teacher lands with the modal already
  // open instead of having to find the Log In button themselves.
  useEffect(() => {
    if (!searchParams.has('sign_in') || !isLoaded) return;
    searchParams.delete('sign_in');
    setSearchParams(searchParams, { replace: true });
    if (!isSignedIn) {
      showSignInModal();
    }
  }, [searchParams, setSearchParams, isLoaded, isSignedIn, showSignInModal]);

  // Shared template query param. A single link can carry several ids
  // (`?sharedTemplate=a,b,c`), so the list is parsed here at the URL boundary and
  // the modal only ever sees an array of ids.
  useEffect(() => {
    const templateIdParam = searchParams.get('sharedTemplate');
    if (!templateIdParam || !isLoaded) return;
    if (!isSignedIn) {
      showSignInModal();
      return;
    }
    const templateIds = [
      ...new Set(
        templateIdParam
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      ),
    ];
    if (templateIds.length > 0) setSharedTemplateIds(templateIds);
    searchParams.delete('sharedTemplate');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, isLoaded, isSignedIn, showSignInModal, setSharedTemplateIds]);

  // Prompt query param pre-fill
  useEffect(() => {
    const promptParam = searchParams.get('prompt');
    if (!promptParam) {
      // Clear the guard once the param is gone, or the same prompt can only ever
      // be applied once per page load. This effect removes `prompt` from the URL
      // after applying it (below), so a second request for the SAME text — a
      // teacher clicking the sidebar ad's prompt CTA again — would arrive to find
      // the ref still holding that text and be skipped. The guard is only there
      // to stop a re-apply while the param is still present.
      appliedPromptRef.current = null;
      return;
    }
    if (appliedPromptRef.current !== promptParam) {
      appliedPromptRef.current = promptParam;
      pendingSuggestionsRef.current = promptParam;
      setQuery(promptParam);
      autocomplete.setSuggestions([]);
      const id = setTimeout(() => {
        pendingTimers.current = pendingTimers.current.filter((t) => t !== id);
        adjustTextareaHeight();
        inputRef.current?.focus();
      }, 0);
      pendingTimers.current.push(id);
    }
    if (!isLoaded) return;
    if (pendingSuggestionsRef.current === promptParam) {
      pendingSuggestionsRef.current = null;
      autocomplete.fetchSuggestions(promptParam);
    }
    // Logged-out users keep ?prompt= in the URL for post-sign-up recovery.
    if (isSignedIn) {
      searchParams.delete('prompt');
      setSearchParams(searchParams, { replace: true });
    }
  }, [
    searchParams,
    setSearchParams,
    isLoaded,
    isSignedIn,
    setQuery,
    autocomplete,
    adjustTextareaHeight,
    inputRef,
  ]);

  // Deep-link menu auto-open (e.g. app.diffit.me/?open=adapt from a marketing link).
  // Opens one of the toolbar menus on landing, then strips the param so it doesn't
  // reopen on subsequent renders. Supports adapt | attach | align. No auth gate —
  // the menu opens for everyone; gated actions inside still go through requireAuth.
  useEffect(() => {
    const menu = searchParams.get('open')?.toLowerCase();
    if (!menu || menu === 'curriculum') return;
    const openers = {
      adapt: setShowAdaptMenu,
      attach: setShowPlusMenu,
      align: setShowStandardsSubmenu,
    };
    const open = openers[menu];
    if (open) {
      open(true);
      Mixpanel.track('Unlimited Menu Auto-Opened', { menu });
    }
    searchParams.delete('open');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, setShowAdaptMenu, setShowPlusMenu, setShowStandardsSubmenu]);

  // Curriculum deep link (?open=curriculum&curriculum=<id>) from the curriculum announcement ad:
  // opens the lesson picker for a curriculum `extra-curricula` gives this teacher, else the Align
  // menu. Waits for LD identify (the anonymous context has none), and registers the published
  // index first because the picker renders only for a registered curriculum.
  useEffect(() => {
    if (searchParams.get('open')?.toLowerCase() !== 'curriculum' || !isLDidentified) return;
    const curriculumId = searchParams.get('curriculum');
    searchParams.delete('open');
    searchParams.delete('curriculum');
    setSearchParams(searchParams, { replace: true });

    Mixpanel.track('Unlimited Menu Auto-Opened', {
      menu: 'curriculum',
      curriculum_id: curriculumId,
    });
    if (!curriculumId || !extraCurricula?.includes(curriculumId)) {
      setShowStandardsSubmenu(true);
      return;
    }
    getCurricula()
      .then((entries) => {
        registerCurricula(toCurricula(entries));
        openCurriculum(curriculumId);
      })
      .catch(() => setShowStandardsSubmenu(true));
  }, [
    searchParams,
    setSearchParams,
    isLDidentified,
    extraCurricula,
    openCurriculum,
    setShowStandardsSubmenu,
  ]);
};
